import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTable, useFilters, usePagination, useSortBy } from "react-table";
import { connect } from "react-redux";
import { compose } from "redux";
import { Redirect } from "react-router-dom";
import { RiTableFill, RiTableLine } from "react-icons/ri";
import { PiCalendarDotsFill, PiCalendarDots } from "react-icons/pi";
import CalendarView from "../../components/Control/CalendarView";
import LOADER from "../../assets/images/loader.svg";
import { getComplianceControls_Report, editControl } from "../../store/actions/riskActions";
import Popup from 'reactjs-popup';
import ControlOverlay from "../../components/Control/ControlOverlay";
import { FiAlertTriangle, FiXCircle, FiClock, FiCheckCircle } from "react-icons/fi";
import ControlsHeader from "../../components/Control/ControlsHeader";
import moment from "moment";
import DraggableChecklistCard from "../../components/Risk/DraggableChecklistCard.js";
import callOpenAIAPI, { callOpenAIAPIChecklist } from "../../utils/openAI.js";

function ComplianceControls(props) {
    const { t } = useTranslation();
    const { token, complianceControls, activeComplianceId, getComplianceControlsReportFN, editControlFN } = props;

    const [loading, setLoading] = useState(false);
    const [flattenedControls, setFlattenedControls] = useState([]);
    const [selectedView, setSelectedView] = useState("table");
    const [isControlOpen, setIsControlOpen] = useState(false);
    const [control_id, setControlId] = useState(null);
    const [risk_id, setRiskId] = useState(null);
    const [query, setQuery] = useState('');
    const [statusView, setStatusView] = useState('All');
    const [effView, setEffView] = useState('All');
    const [viewMode, setViewMode] = useState("table");
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [loadingAI, setLoadingAI] = useState(false); // For loading state during API call
    const [showChecklist, setShowCheklist] = useState(false); // Tracks when to show controls with animation
    const [checklist, setChecklist] = useState([]);
    const [findingId, setFindingID] = useState(null);
    


    const onRemoveControl = (id) => {
        setChecklist(prevControls => prevControls.filter(c => c.id !== id));
    };


    const typePillClass = (t) => {
        const key = (t || "").toString().trim().toLowerCase();
        if (key === "preventive") return "pill-blue";
        if (key === "detective") return "pill-purple";
        if (key === "corrective") return "pill-orange";
        if (key === "mitigative") return "pill-red";
        if (key === "directive") return "pill-green";
        return "badge-soft-gray";
    };

    const effBucket = (n) => {
        const v = Number(n ?? 0);
        if (v >= 75) return 'high';
        if (v >= 40) return 'medium';
        return 'low';
    };

    useEffect(() => {
        if (activeComplianceId) {
            setLoading(true);
            getComplianceControlsReportFN(activeComplianceId).finally(() => setLoading(false));
        }
    }, [activeComplianceId, getComplianceControlsReportFN]);

    useEffect(() => {
        if (complianceControls && complianceControls?.length > 0) {
            const allControls = complianceControls
                .filter((control) =>
                    control.controls.some(
                        (c) =>
                            c.controlDetails !== null ||
                            c.controlType !== null ||
                            c.controlDefinition !== null
                    )
                )
                .flatMap((control) =>
                    control.controls.map((c) => ({
                        ...c,
                        riskId: control.riskId,
                        title: control.title,
                        owner: control.owner,
                    }))
                );
            setFlattenedControls(allControls);
        } else {
            setFlattenedControls([]);
        }
    }, [complianceControls]);

    const columns = React.useMemo(
        () => [
            {
                Header: "Control Title",
                accessor: "controlDetails",
                Cell: ({ value }) => (
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word", width: "400px" }}>
                        {value}
                    </div>
                ),
            },
            { Header: "Control Type", accessor: "controlType" },
            {
                Header: "Control Efficiency",
                accessor: "efficiencyLevel",
                Cell: ({ value }) => (
                    <div className="relative w-32 h-6 bg-gray-200 rounded-full flex items-center">
                        <div
                            className="absolute h-full rounded-full"
                            style={{
                                width: `${value || 0}%`,
                                backgroundColor:
                                    value >= 75 ? "#00AFB9" : value >= 50 ? "#FFD166" : "#EF476F",
                            }}
                        />
                        <span className="relative text-white ml-2 text-xs">
                            {value ? `${parseFloat(value).toFixed(0)}%` : "0%"}
                        </span>
                    </div>
                ),
            },
            { Header: "Control Period", accessor: "controlPeriod" },
            { Header: "Risk Title", accessor: "title" },
            {
                Header: "Owner",
                accessor: (row) =>
                    `${row.owner?.firstName || ""} ${row.owner?.lastName || ""}`,
                id: "owner",
            },
        ],
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        gotoPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: flattenedControls,
            initialState: { pageIndex: 0, pageSize: 10 },
        },
        useFilters,
        useSortBy,
        usePagination
    );


     const closeCreation = async (data, control_id) => {
        ////console.log("Data:", data, "ID", control_id)

        setIsControlOpen(false)
        await updateServerData(data, control_id);
        getComplianceControlsReportFN(activeComplianceId)

    }

    const updateServerData = async (data, control_id) => {


        const updatedData = { ...data };

        // Add one day to StartDate and EndDate if they exist
        if (updatedData.StartDate) {
            const startDate = new Date(updatedData.StartDate);
            startDate.setDate(startDate.getDate() + 1);
            updatedData.StartDate = startDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        }

        if (updatedData.EndDate) {
            const endDate = new Date(updatedData.EndDate);
            endDate.setDate(endDate.getDate() + 1);
            updatedData.EndDate = endDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        }

        ////console.log("CONTROL DATA AFTER ADJUSTMENT", updatedData);

        setLoading(true);
        await editControlFN(risk_id, control_id, updatedData);
        setLoading(false);

    }

    const controlToRisk = useMemo(() => {
        const m = new Map();
        for (const risk of (complianceControls ?? [])) {
            const rid = risk.riskId ?? risk.risk_id;
            for (const ctrl of (risk.controls ?? [])) {
                const cid = String(ctrl.controlID ?? ctrl.control_id ?? ctrl.id);
                if (cid !== "undefined") m.set(cid, rid);
            }
        }
        return m;
    }, [complianceControls]);

    const renderPagination = () => {
        const total = flattenedControls?.length || 0;
        const from = total === 0 ? 0 : pageIndex * pageSize + 1;
        const to = Math.min((pageIndex + 1) * pageSize, total);
        const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
        return (
        <div className="pagination flex flex-col sm:flex-row flex-wrap justify-center sm:justify-between items-center gap-2 mt-4 text-sm text-gray-600">
            <div>
                {t("controlManagement.pagination.showingRange", { from, to, total })}
            </div>
            <div className="flex items-center space-x-2">
                <button
                    onClick={() => gotoPage(0)}
                    disabled={!canPreviousPage}
                    className={`pagination-button ${!canPreviousPage ? "disabled" : ""}`}
                >
                    ┬½ {t("controlManagement.pagination.first")}
                </button>
                <button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    className={`pagination-button ${!canPreviousPage ? "disabled" : ""}`}
                >
                    {t("controlManagement.pagination.previous")}
                </button>
                <span>
                    {t("controlManagement.pagination.pageXofY", { current: pageIndex + 1, total: totalPages })}
                </span>
                <button
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    className={`pagination-button ${!canNextPage ? "disabled" : ""}`}
                >
                    {t("controlManagement.pagination.next")} ┬╗
                </button>
                <button
                    onClick={() => gotoPage(Math.max(0, totalPages - 1))}
                    disabled={!canNextPage}
                    className={`pagination-button ${!canNextPage ? "disabled" : ""}`}
                >
                    {t("controlManagement.pagination.last")} ┬╗
                </button>
            </div>
            <div className="ml-0 sm:ml-4 flex flex-wrap items-center gap-x-2">
                <label>
                    {t("controlManagement.pagination.pageSizeLabel")}:
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="ml-2 border rounded-md p-1"
                    >
                        {[10, 20, 30, 40, 50].map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                </label>
                <span className="text-gray-500">
                    {t("controlManagement.pagination.perPage", { size: pageSize })}
                </span>
            </div>
        </div>
        );
    };


     const fetchAISuggestions = async (title, details) => {
        setShowCheklist(false);
        setLoadingAI(true);
    
        try {
          const aiResponse = await callOpenAIAPIChecklist(title, details);
    
          ////console.log("aiResponse1", aiResponse);
    
          const parsedControls = aiResponse.map((item, i) => ({
            id: crypto.randomUUID?.() || `control-${i}`,
            text: item,   // store the string under "text"
          }));
    
          setChecklist(parsedControls);
    
          ////console.log("aiResponse", parsedControls);
    
          setTimeout(() => {
            setShowCheklist(true);
          }, 300);
        } catch (error) {
          console.error("Error fetching AI suggestions:", error);
        } finally {
          setLoadingAI(false);
        }
      };

    const handleIconClick = (view) => setSelectedView(view);

    function StatCard({ label, value }) {
        let Icon;
        let iconColor = "";

        switch (label) {
            case "Total Findings":
                Icon = FiAlertTriangle;
                iconColor = "text-gray-400";
                break;
            case "In Progress":
                Icon = FiXCircle;
                iconColor = "text-red-500";
                break;
            case "Open Findings":
                Icon = FiClock;
                iconColor = "text-blue-500";
                break;
            case "Closed":
                Icon = FiCheckCircle;
                iconColor = "text-green-500";
                break;
            default:
                Icon = null;
        }

        return (
            <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">{label}</div>
                    {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
                </div>
                <div className="mt-1 text-2xl text-[#0E0E0E]">{value}</div>
            </div>
        );
    }
    const [typeView, setTypeView] = useState('All');

    const counts = useMemo(() => {
        const normType = (s = "") => s?.toString().trim().toLowerCase();
        let total = flattenedControls?.length || 0;
        let highEff = 0;
        let preventive = 0;
        let detective = 0;

        (flattenedControls || []).forEach((c) => {
            const eff = Number(c.efficiencyLevel ?? 0);
            if (eff >= 75) highEff++;
            const t = normType(c.controlType);
            if (t === "preventive") preventive++;
            if (t === "detective") detective++;
        });

        return { total, highEff, preventive, detective };
    }, [flattenedControls]);




      const filteredControls = useMemo(() => {
        const q = (query || "").toLowerCase().trim();
        const norm = (v) => (v ?? "").toString().toLowerCase();
        const wantType = (typeView || "All").toLowerCase();
        const wantStatus = (statusView || "All").toLowerCase();
        const wantEff = (effView || "All").toLowerCase();
    
        const statusOf = (c) => {
          const now = new Date();
          const s = c.startDate ? new Date(c.startDate) : null;
          const e = c.endDate ? new Date(c.endDate) : null;
          if (e && e < now) return "closed";
          if (s && s > now) return "open";
          return "in progress";
        };
    
        return (flattenedControls || []).filter((c) => {
          // text match
          const hay = [
            norm(c.title),
            norm(c.controlDetails),
            norm(c.controlDefinition),
            norm(Array.isArray(c.complianceTypes) ? c.complianceTypes.join(",") : c.complianceTypesRaw || ""),
            norm(c.owner?.firstName), norm(c.owner?.lastName),
            norm(c.controlType),
          ].join(" ");
          if (q && !hay.includes(q)) return false;
    
          // type filter
          if (wantType !== "all" && (c.controlType || "").toLowerCase() !== wantType) return false;
    
          // status filter
          if (wantStatus !== "all" && statusOf(c) !== wantStatus) return false;
    
          // efficiency filter
          if (wantEff !== "all" && effBucket(c.efficiencyLevel) !== wantEff) return false;
    
          return true;
        });
      }, [flattenedControls, query, typeView, statusView, effView]);

    return token ? (
        <div className="w-full min-h-screen bg-[#F8F8F8] flex flex-col">
            {/* ­şöÑ Scrolling happens inside here */}
            <div className="flex-1 overflow-y-auto">
                {/* inner page container */}
                <div className="relative px-6 md:px-10 xl:px-16 2xl:px-24 pt-8 md:pt-5 pb-40">
                    {/* Top header + Table / Calendar toggle */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-xl md:text-2xl font-medium tracking-wide text-[#0E0E0E]">
                                {selectedView === "calendar"
                                    ? t("controlManagement.calendarTitle")
                                    : t("controlManagement.title")}
                            </h1>
                            <p className="text-xs md:text-sm text-gray-600">
                                {selectedView === "calendar"
                                    ? t("controlManagement.calendarSubtitle")
                                    : t("controlManagement.subtitle")}
                            </p>
                        </div>

                        {/* Right: View toggle buttons (Table / Calendar) */}
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => handleIconClick("table")}
                                className={`flex items-center gap-2 border rounded-lg px-3.5 py-1.5 text-sm font-medium transition 
                ${selectedView === "table"
                                        ? "bg-[#F3F4F6] text-[#0E0E0E] border-gray-300 shadow-sm"
                                        : "bg-white text-gray-600 hover:bg-gray-50 border-gray-300"
                                    }`}
                            >
                                {selectedView === "table" ? (
                                    <RiTableFill size={18} className="text-[#0E0E0E]" />
                                ) : (
                                    <RiTableLine size={18} className="text-gray-500" />
                                )}
                                <span>{t("controlManagement.toggleControls")}</span>
                            </button>

                            <button
                                onClick={() => handleIconClick("calendar")}
                                className={`flex items-center gap-2 border rounded-lg px-3.5 py-1.5 text-sm font-medium transition 
                ${selectedView === "calendar"
                                        ? "bg-[#F3F4F6] text-[#0E0E0E] border-gray-300 shadow-sm"
                                        : "bg-white text-gray-600 hover:bg-gray-50 border-gray-300"
                                    }`}
                            >
                                {selectedView === "calendar" ? (
                                    <PiCalendarDotsFill size={18} className="text-[#0E0E0E]" />
                                ) : (
                                    <PiCalendarDots size={18} className="text-gray-500" />
                                )}
                                <span>{t("controlManagement.toggleCalendar")}</span>
                            </button>
                        </div>
                    </div>

                    {/* LOADING STATE */}
                    {loading ? (
                        <div className="w-full flex justify-center items-center py-16">
                            <img src={LOADER} alt="Loading..." className="spinner" />
                        </div>
                    ) : (
                        <>
                            {/* TABLE MODE CONTENT */}
                            {selectedView === "table" && (
                                <>
                                    {/* Stat Cards */}
                                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                        <StatCard label={t("controlManagement.summary.totalControls")} value={counts.total} />
                                        <StatCard label={t("controlManagement.summary.highEffectiveness")} value={counts.highEff} />
                                        <StatCard label={t("controlManagement.summary.preventive")} value={counts.preventive} />
                                        <StatCard label={t("controlManagement.summary.detective")} value={counts.detective} />
                                    </div>

                                    {/* Filters + viewMode toggle */}
                                    <div className="mt-5">
                                        <ControlsHeader
                                            query={query}
                                            setQuery={setQuery}
                                            typeView={typeView}
                                            setTypeView={setTypeView}
                                            effView={effView}
                                            setEffView={setEffView}
                                            statusView={statusView}
                                            setStatusView={setStatusView}
                                            viewMode={viewMode}
                                            setViewMode={setViewMode}
                                            showToggle={true}
                                        />
                                    </div>

                                    {/* GRID vs TABLE SWITCH */}
                                    {viewMode === "grid" ? (
                                        <>
                                            <div className="mt-4 shadow-xl bg-white rounded-2xl border border-gray-200 overflow-hidden">

                                                {/* Header ÔÇö divider REMOVED */}
                                                <div className="px-6 py-4 bg-white">
                                                    <h3 className="text-gray-900">Controls List</h3>
                                                    <p className="text-sm text-gray-500">
                                                        {(filteredControls?.length ?? 0)} controls found
                                                    </p>
                                                </div>

                                                {/* ­şæç Wrap grid + divider inside SAME padding block */}
                                                <div className="px-4 pb-4">

                                                    {/* Grid wrapper inside the rounded card */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 py-4 items-stretch">
                                                        {page.map((row) => {
                                                            prepareRow(row);
                                                            const control = row.original;

                                                            const num = Number(control.findingNumber ?? 0);
                                                            const findingCls = isNaN(num)
                                                                ? ""
                                                                : num === 0
                                                                    ? "badge-green"
                                                                    : "badge-red";

                                                            const effRaw = Number(
                                                                control.efficiencyLevel ??
                                                                control.controlEfficiency ??
                                                                control.efficiency ??
                                                                0
                                                            );
                                                            const effLabel =
                                                                effRaw >= 75 ? "High" : effRaw >= 40 ? "Medium" : "Low";
                                                            const effColorClass =
                                                                effRaw >= 75
                                                                    ? "bg-green-700 text-white"
                                                                    : effRaw >= 40
                                                                        ? "bg-yellow-600 text-white"
                                                                        : "bg-red-700 text-white";

                                                            const ownerName =
                                                                `${control.owner?.firstName || ""} ${control.owner?.lastName || ""
                                                                    }`.trim() || "-";

                                                            return (
                                                                <div
                                                                    key={control.controlID}
                                                                    onClick={() => {
                                                                        const matchingRisk = complianceControls.find((risk) =>
                                                                            risk.controls?.some(
                                                                                (ctrl) => ctrl.controlID === control.controlID
                                                                            )
                                                                        );
                                                                        if (matchingRisk) setRiskId(matchingRisk.riskId);
                                                                        setControlId(control.controlID);
                                                                        setTimeout(() => setIsControlOpen(true), 0);
                                                                    }}
                                                                    className="bg-white border border-gray-200 rounded-lg px-2 py-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex flex-col min-h-[280px]"
                                                                >
                                                                    <div className="flex items-start justify-between mb-1.5">
                                                                        <span className="text-xs text-gray-500">{`CTRL-${control.controlID ?? ""
                                                                            }`}</span>
                                                                        {control.controlPeriod && (
                                                                            <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-700">
                                                                                {control.controlPeriod}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <h4 className="font-medium text-gray-800 mb-2 line-clamp-2">
                                                                        {control.controlDetails || "-"}
                                                                    </h4>

                                                                    {control.controlDefinition && (
                                                                        <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                                                                            {control.controlDefinition}
                                                                        </p>
                                                                    )}

                                                                    <div className="flex flex-col gap-1.5 mb-2">
                                                                        <div className="flex items-center gap-1.5">
                                                                            {control.controlType && (
                                                                                <span
                                                                                    className={`px-2 py-1 rounded-lg text-xs ${typePillClass(
                                                                                        control.controlType
                                                                                    )}`}
                                                                                >
                                                                                    {control.controlType}
                                                                                </span>
                                                                            )}
                                                                            <span
                                                                                className={`${effColorClass} px-1.5 py-1 rounded text-xs font-medium`}
                                                                            >
                                                                                {effLabel}
                                                                            </span>
                                                                        </div>

                                                                        <div className="flex flex-wrap gap-0.5 justify-start">
                                                                            {Array.isArray(control.complianceTypes) &&
                                                                                control.complianceTypes?.length > 0 ? (
                                                                                control.complianceTypes.map((c, i) => (
                                                                                    <span
                                                                                        key={i}
                                                                                        className="bg-gray-100 text-gray-700 border border-gray-300 px-1.5 py-0.5 rounded text-xs"
                                                                                    >
                                                                                        {c}
                                                                                    </span>
                                                                                ))
                                                                            ) : (
                                                                                <span className="text-gray-400 text-xs">None</span>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    <div className="flex flex-col gap-1 pt-2 border-t border-gray-100 mt-auto">
                                                                        <div>
                                                                            <span className="text-xs text-gray-500">Owner: </span>
                                                                            <span className="text-xs text-gray-700">{ownerName}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="text-xs text-gray-500">Findings: </span>
                                                                            <span className={`text-xs ${isNaN(num) ? "" : findingCls}`}>
                                                                                {isNaN(num) ? "-" : num}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="text-xs text-gray-500">Last Reviewed: </span>
                                                                            <span className="text-xs text-gray-700">
                                                                                {control.lastReviewDate
                                                                                    ? new Date(control.lastReviewDate)
                                                                                        .toLocaleDateString("en-GB", {
                                                                                            day: "2-digit",
                                                                                            month: "2-digit",
                                                                                            year: "numeric",
                                                                                        })
                                                                                        .replace(/\//g, ".")
                                                                                    : control.reviewDate
                                                                                        ? new Date(control.reviewDate)
                                                                                            .toLocaleDateString("en-GB", {
                                                                                                day: "2-digit",
                                                                                                month: "2-digit",
                                                                                                year: "numeric",
                                                                                            })
                                                                                            .replace(/\//g, ".")
                                                                                        : "-"}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* Divider + pagination INSIDE the same px-4 wrapper */}
                                                    <div className="border-t border-gray-200 mt-3 pt-3 mb-4">
                                                        {renderPagination()}
                                                    </div>

                                                </div>
                                            </div>
                                        </>




                                    ) : (
                                        <>
                                            <div className="shadow-xl bg-white rounded-2xl border border-gray-200 mt-6">

                                                {/* Header */}
                                                <div className="px-6 py-4">
                                                    <h3 className="text-gray-900">Controls List</h3>
                                                    <p className="text-sm text-gray-500">
                                                        {(filteredControls?.length ?? 0)} controls found (Page {pageIndex + 1} of{" "}
                                                        {Math.max(1, Math.ceil((flattenedControls?.length || 0) / pageSize))})
                                                    </p>
                                                </div>

                                                {/* Inner padded content */}
                                                <div className="px-4 pt-4 pb-4">

                                                    {/* Table wrapper */}
                                                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                                                        <table
                                                            {...getTableProps()}
                                                            className="w-full text-sm text-gray-800"
                                                            style={{
                                                                borderCollapse: "separate",
                                                                borderSpacing: 0,
                                                                tableLayout: "fixed",
                                                            }}
                                                        >
                                                            <thead>
                                                                {headerGroups.map((headerGroup) => {
                                                                    const cols = headerGroup.headers;

                                                                    return (
                                                                        <tr
                                                                            {...headerGroup.getHeaderGroupProps()}
                                                                            className="border-b border-gray-300 bg-white"
                                                                        >
                                                                            {/* Control ID (static first column) */}
                                                                            <th
                                                                                className="text-left text-[13px] text-gray-800 font-semibold"
                                                                                style={{
                                                                                    background: "#fff",
                                                                                    padding: "0.3rem 0.75rem",
                                                                                    width: "110px",
                                                                                    minWidth: "110px",
                                                                                    maxWidth: "110px",

                                                                                    whiteSpace: "nowrap",
                                                                                }}
                                                                            >
                                                                                Control ID
                                                                            </th>

                                                                            {/* Dynamic headers from columns[] */}
                                                                            {cols.map((column) => {
                                                                                if (column.Header === "Risk Title") return null;

                                                                                const w = column.columnWidth;

                                                                                return (
                                                                                    <th
                                                                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                                                                        className="cursor-pointer text-left text-[13px] text-gray-800 font-semibold"
                                                                                        style={{
                                                                                            background: "#fff",
                                                                                            padding: "0.3rem 0.75rem",
                                                                                            width: w ? `${w}px` : "auto",
                                                                                            minWidth: w ? `${w}px` : undefined,
                                                                                            maxWidth: w ? `${w}px` : undefined,
                                                                                        }}
                                                                                    >
                                                                                        <div className="flex items-center gap-1">
                                                                                            {column.render("Header")}
                                                                                            <span className="opacity-70">
                                                                                                {column.isSorted
                                                                                                    ? column.isSortedDesc
                                                                                                        ? " ­şö¢"
                                                                                                        : " ­şö╝"
                                                                                                    : ""}
                                                                                            </span>
                                                                                        </div>
                                                                                    </th>
                                                                                );
                                                                            })}
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </thead>


                                                            <tbody {...getTableBodyProps()}>
                                                                {page?.length === 0 ? (
                                                                    /* your empty state here */
                                                                    <>
                                                                        {/* ... unchanged ... */}
                                                                    </>
                                                                ) : (
                                                                    page.map((row) => {
                                                                        prepareRow(row);
                                                                        return (
                                                                            <tr
                                                                                {...row.getRowProps({
                                                                                    onClick: () => {
                                                                                        const matchingRisk = complianceControls.find((risk) =>
                                                                                            risk.controls?.some(
                                                                                                (ctrl) => ctrl.controlID === row.original.controlID
                                                                                            )
                                                                                        );
                                                                                        if (matchingRisk) setRiskId(matchingRisk.riskId);
                                                                                        setControlId(row.original.controlID);
                                                                                        setTimeout(() => setIsControlOpen(true), 0);
                                                                                    },
                                                                                })}
                                                                                className="group transition-colors duration-200 cursor-pointer"
                                                                            >
                                                                                {/* Control ID */}
                                                                                <td
                                                                                    className="border-t border-gray-200 group-hover:bg-[#F8F8F8] whitespace-nowrap"
                                                                                    style={{ padding: "0.65rem 1rem", width: 110 }}
                                                                                >
                                                                                    {`CTRL-${row.original.controlID ?? ""}`}
                                                                                </td>

                                                                                {/* Dynamic cells */}
                                                                                {row.cells.map((cell) => {
                                                                                    if (cell.column.Header === "Risk Title") return null;

                                                                                    return (
                                                                                        <td
                                                                                            {...cell.getCellProps()}
                                                                                            className="border-t border-gray-200 group-hover:bg-[#F8F8F8] align-top"
                                                                                            style={{ padding: "0.65rem 1rem" }}
                                                                                        >
                                                                                            {cell.render("Cell")}
                                                                                        </td>
                                                                                    );
                                                                                })}
                                                                            </tr>
                                                                        );
                                                                    })
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Divider + pagination ÔÇô no extra padding under line */}
                                                    <div className="border-t border-gray-200 mt-3">
                                                        {renderPagination()}
                                                    </div>
                                                </div>
                                            </div>
                                        </>






                                    )}

                                </>
                            )}

                            {/* CALENDAR VIEW CONTENT */}
                            {selectedView === "calendar" && (
                                <>
                                    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-2 mb-4 shadow-sm">
                                        <div className="flex items-center justify-end">
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => {
                                                        const newDate = moment(calendarDate)
                                                            .subtract(1, "month")
                                                            .toDate();
                                                        setCalendarDate(newDate);
                                                    }}
                                                    className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition"
                                                >
                                                    <span>{t("controlManagement.pagination.previous")}</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const newDate = moment(calendarDate)
                                                            .add(1, "month")
                                                            .toDate();
                                                        setCalendarDate(newDate);
                                                    }}
                                                    className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition"
                                                >
                                                    <span>{t("controlManagement.pagination.next")}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-12 flex justify-center">
                                        <div className="w-full max-w-[1700px]">
                                            <CalendarView
                                                events={filteredControls}
                                                currentDate={calendarDate}
                                                onNavigate={setCalendarDate}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* Control Popup */}
                    <Popup
                        open={isControlOpen}
                        onClose={() => setIsControlOpen(false)}
                        modal
                        closeOnDocumentClick={false}
                        closeOnEscape
                        contentStyle={{
                            overflow: "auto",
                            maxHeight: "80vh",
                            width: "900px",
                            marginTop: "14vh",
                        }}
                    >
                        <ControlOverlay
                            key={control_id || "new-control"}
                            closeControl={closeCreation}
                            control_id={control_id}
                            risk_id={risk_id}
                            fetchAISuggestions={fetchAISuggestions}
                            loadingAI={loadingAI}
                            setFindingID={setFindingID}
                        />
                    </Popup>

                    {/* Floating AI Suggested Controls */}
                    {checklist?.length > 0 && (
                        <div
                            className={`absolute right-6 top-28 w-[340px] bg-transparent rounded-md p-4 transition-all duration-500 ease-in-out ${showChecklist ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                                }`}
                            style={{ zIndex: 1050, pointerEvents: "auto" }}
                        >
                            {checklist.map((control) => (
                                <DraggableChecklistCard
                                    key={control.id}
                                    control={control}
                                    onRemove={() => onRemoveControl(control.id)}
                                />
                            ))}
                        </div>
                    )}

                    {/* tiny spacer to guarantee extra scroll */}
                    <div className="h-6" />
                </div>
            </div>
        </div>
    ) : (
        <Redirect to="/login" />
    );
}

const mapStateToProps = (state) => {
    ////console.log("Conoplince controls", state.risk.compliance_controls)
    return {
        token: state.auth.token,
        complianceControls: state.risk.compliance_controls,
        activeComplianceId: state.compliance.activeComplianceId,
    }
};

const mapDispatchToProps = (dispatch) => ({
    getComplianceControlsReportFN: (complianceId) => dispatch(getComplianceControls_Report(complianceId)),
    editControlFN: (risk_id, control_id, control) => dispatch(editControl(risk_id, control_id, control)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(ComplianceControls);